import { SignedInUser } from "@revelate/types";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

const apiUrl = import.meta.env.VITE_API_URL;

export function getProviderSyncUrl(
  provider: string,
  companyId: number,
  providerId?: string
) {
  return (
    import.meta.env.VITE_API_URL +
    `/providers/${provider}/deals/${companyId}` +
    `${providerId ? `/${providerId}` : ""}`
  );
}

export const getDateRangeSyncUrl = (
  company_id: number,
  dateRange: DateRange
) => {
  const { from: fromStr, to: toStr } = dateRange;

  const from = fromStr ? format(fromStr, "yyyy-MM-dd") : "";
  const to = toStr ? format(toStr, "yyyy-MM-dd") : "";
  return (
    import.meta.env.VITE_API_URL +
    `/companies/${company_id}/sync?from=${from}&to=${to}`
  );
}

export const getMagicLinkUrl = (email: string) => {
  return import.meta.env.VITE_API_URL + `/users/magiclink/${email}`;
};

export async function commissionAuthorization() {
  const { supabaseClient } = await import("@/lib/supabase/client");
  const { data, error } = await supabaseClient.auth.getSession();
  if (error || !data.session) throw new Error("Please sign in again.");
  return `Bearer ${data.session.access_token}`;
}

export async function getAPI(path: string, cache = true) {
  const url = `${apiUrl}/${path}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: path.startsWith("calc/commissions/") ? await commissionAuthorization() : "Bearer " + import.meta.env.VITE_API_KEY,
    },
    cache: cache ? "default" : "no-cache",
  });

  if (!response.ok) {
    console.error("API request failed: ", await response.text());
    return {};
  }

  return response.json();
}

export const postAPI = async (
  path: string,
  body: string,
  cache = true,
  onError?: () => void
) => {
  const result = await fetch(`${apiUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + import.meta.env.VITE_API_KEY,
    },
    body,
    cache: cache ? "default" : "no-cache",
  });

  if (!result.ok) {
    console.log("Error with API request: ", await result.text());
    onError && onError();
    return null;
  }
  return await result.json();
};

export const postAuthenticatedAPI = async <TResponse>(
  path: string,
  body: unknown,
  accessToken: string,
  signal?: AbortSignal,
): Promise<TResponse> => {
  const result = await fetch(`${apiUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
    cache: "no-cache",
    signal,
  });

  const responseBody = await result.json().catch(() => null) as TResponse | {
    error?: string;
    message?: string;
  } | null;
  if (!result.ok) {
    const errorBody = responseBody
      && typeof responseBody === "object"
      ? responseBody as { error?: string; message?: string }
      : null;
    const error = new Error(
      errorBody?.message
        ? errorBody.message
        : "Authenticated API request failed.",
    );
    Object.assign(error, {
      code: errorBody?.error
        ? errorBody.error
        : "request_failed",
      status: result.status,
    });
    throw error;
  }

  return responseBody as TResponse;
};

export const patchAPI = async (
  path: string,
  body: string,
  cache = true,
  onError?: () => void
) => {
  const result = await fetch(`${apiUrl}${path}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + import.meta.env.VITE_API_KEY,
    },
    body,
    cache: cache ? "default" : "no-cache",
  });

  if (!result.ok) {
    console.log("Error with API request: ", await result.text());
    onError && onError();
    return null;
  }
  return await result.json();
};

export const getCalcUrl = (
  currentUser: SignedInUser | null,
  dateRange?: DateRange,
  userIds?: string[],
  isProjected?: boolean,
  isEphemeral?: boolean,
  logResult?: boolean
) => {
  if (!currentUser || !dateRange) return null;

  const companyId = currentUser.company_id;
  const userIdsParam = userIds && userIds.length > 0 ? userIds?.map((id) => `userIds=${id}`).join("&") : "";
  // Ensure from and to are proper Date objects before formatting
  const fromParam = dateRange.from
    ? `&from=${format(dateRange.from instanceof Date ? dateRange.from : new Date(dateRange.from), "yyyy-MM-dd")}`
    : "";

  const toParam = dateRange.to
    ? `&to=${format(dateRange.to instanceof Date ? dateRange.to : new Date(dateRange.to), "yyyy-MM-dd")}`
    : "";

  const projectedParam = isProjected ? "&isProjected=true" : "";
  const ephemeralParam = isEphemeral ? "&ephemeral=true" : "";
  const logParam = logResult ? "&logResult=true" : "";

  return `calc/commissions/${companyId}?`
    + userIdsParam
    + fromParam
    + toParam
    + projectedParam
    + ephemeralParam
    + logParam;
};
